<template>
  <div class="earliest-date-box">
    <div class="header-box">
      <appointment-filter
        class="filter"
        :filter-info="filterInfo"
        :config-info="configInfo"
        :total="appointmentList.length"
        :loading="loadingInfo.list"
        :disabled="loadingInfo.config"
        @change="onFilterChange"
      />
    </div>
    <div class="content-box">
      <div class="title-wrap">
        <div class="title-box">
          <div class="country-column">国家 / 签证类型</div>
          <div class="city-column">城市</div>
          <div class="date-column">最早可预约日期</div>
          <div class="action-column">操作</div>
        </div>
      </div>
      <div v-if="loadingInfo.config || loadingInfo.list" class="status-box">
        查询中...
      </div>
      <div v-else-if="appointmentList.length" class="list-box">
        <div
          v-for="item in appointmentList"
          :key="item.appointment_id"
          class="list-item"
        >
          <div class="country-column">
            <div class="country-info">
              <img
                v-if="item.national_flag_img"
                class="flag"
                :src="item.national_flag_img"
                alt=""
                loading="lazy"
              />
              <span class="country-name">{{ item.country }}</span>
            </div>
            <div class="visa-type-name">{{ item.show_visa_type }}</div>
          </div>
          <div class="city-column">
            {{ configInfo.city_set[item.city_id] || '—' }}
          </div>
          <div class="date-column">
            <div v-if="item.earliest_date" class="appointment-date">
              <span>{{ formatDate(item.earliest_date, 'YYYY-MM-DD') }}</span>
              <span class="week">{{
                formatDate(item.earliest_date, 'ddd')
              }}</span>
            </div>
            <div v-else class="no-appointment">暂无可预约位置</div>
            <div v-if="item.update_time" class="update-time">
              最后更新 {{ formatDate(item.update_time, 'YYYY-MM-DD') }}
            </div>
          </div>
          <div class="action-column">
            <div class="apply-link" @click="gotoCountry(item)">
              <span>立即办理</span>
              <img
                class="arrow-icon"
                src="https://pbs-public.oss-cn-shenzhen.aliyuncs.com/static-visa/pdvwxa/earliest-date/icon-right.png"
                alt=""
              />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="status-box empty-state">
        <div class="empty-image"></div>
        <span>抱歉！暂无信息</span>
      </div>
    </div>
    <div class="footer-box">
      <div class="tips">
        <img
          class="tips-icon"
          src="https://pbs-public.oss-cn-shenzhen.aliyuncs.com/static-visa/ssqpeer/common/icon-remind.png"
          alt=""
        />
        <span
          >可预约日期随时变化，仅供参考。请以官方预约系统为准，如有疑问请及时联系签证专家。</span
        >
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import Http from '@/services/http';
import env from '@/services/dev';
import Base64 from '../activity/base64.js';
import AppointmentFilter from './components/appointment-filter.vue';
import FunctionService from './services/function-service';

// 路由与选项中的 ID 统一为数字，0 表示不限。
function normalizeId(value) {
  return Number(value) || null;
}

export default {
  components: { AppointmentFilter },
  data() {
    return {
      // 城市未传时使用配置默认值，显式传 0 时保留全部城市。
      routeParams: {
        city_id:
          this.$route.query.city_id === undefined
            ? undefined
            : normalizeId(this.$route.query.city_id),
        visa_country_id: normalizeId(this.$route.query.visa_country_id),
        show_visa_type_id: normalizeId(this.$route.query.show_visa_type_id),
        is_have_date: Number(this.$route.query.is_have_date) === 1,
      },
      filterInfo: {
        city_id: null,
        visa_country_id: null,
        show_visa_type_id: null,
        is_have_date: false,
      },
      configInfo: { city_id: null, city_set: {}, visa_country_set: [] },
      appointmentList: [],
      loadingInfo: { config: true, list: false },
      // 保留 APP、M 站的既有跳转和嵌入协议。
      pageInfo: {
        is_in_app: Number(this.$route.query.is_in_app) || 0,
        is_in_m: Number(this.$route.query.is_in_m) || 0,
      },
    };
  },
  created() {
    this.requests = FunctionService.createLatestRunner();
    document.title = '最早可预约日期';
    this.init();
  },
  beforeDestroy() {
    this.requests.invalidateAll();
  },
  methods: {
    // 使用项目已有的中文 dayjs，日期和星期与小程序展示一致。
    formatDate(value, format) {
      return dayjs(value).format(format);
    },
    // 保留原始配置，筛选选项由子组件派生。
    async init() {
      this.requests.invalidateAll();
      this.configInfo = { city_id: null, city_set: {}, visa_country_set: [] };
      this.filterInfo = {
        city_id: null,
        visa_country_id: null,
        show_visa_type_id: null,
        is_have_date: false,
      };
      this.appointmentList = [];
      this.loadingInfo = { config: true, list: false };
      const task = this.requests.run('config', () =>
        Http.newPost('h5/visa_appointment_config_get', {}),
      );
      try {
        const data = await task;
        const { routeParams } = this;
        // URL 中的展示类型必须属于当前国家，避免恢复旧链接或过期条件。
        const selectedCountry = data.visa_country_set.find(
          (item) => item.visa_country_id === routeParams.visa_country_id,
        );
        // 原始签证类型与展示类型不是同一个 ID，不使用旧字段兜底。
        const showVisaTypeId =
          selectedCountry &&
          selectedCountry.appointment_visa_type_config_set.some(
            (item) => item.show_visa_type_id === routeParams.show_visa_type_id,
          )
            ? routeParams.show_visa_type_id
            : null;
        this.configInfo = data;
        this.filterInfo = {
          city_id:
            routeParams.city_id === undefined
              ? normalizeId(data.city_id)
              : routeParams.city_id,
          visa_country_id: routeParams.visa_country_id,
          show_visa_type_id: showVisaTypeId,
          is_have_date: routeParams.is_have_date,
        };
        return this.getData();
      } catch (error) {
        // HTTP 已提示真实失败，失效任务也不再回写页面。
      } finally {
        if (this.requests.isLatest('config', task))
          this.loadingInfo.config = false;
      }
    },
    // URL 和父窗口使用完整快照，0 明确表示全部。
    getFilterParams() {
      return {
        city_id: this.filterInfo.city_id || 0,
        visa_country_id: this.filterInfo.visa_country_id || 0,
        show_visa_type_id: this.filterInfo.show_visa_type_id || 0,
        is_have_date: this.filterInfo.is_have_date ? 1 : 0,
      };
    },
    // 地址记录筛选状态，不增加筛选操作的浏览器历史条目。
    syncFilterParams() {
      const params = this.getFilterParams();
      const location = { query: { ...this.$route.query, ...params } };
      // 回写时移除旧协议字段，避免同一 URL 保存两套类型和日期条件。
      delete location.query.visa_type_id;
      delete location.query.only_available;
      if (
        this.$router.resolve(location).route.fullPath !== this.$route.fullPath
      ) {
        this.$router.replace(location).catch(() => {
          // 页面离开或导航被替代不影响当前查询。
        });
      }
      if (this.pageInfo.is_in_m) {
        window.parent.postMessage({ type: 'change_filter', data: params }, '*');
      }
    },
    // 组合查询、排序和统计由后端统一完成，只接收最新一次筛选结果。
    async getData() {
      const { filterInfo } = this;
      const params = {
        city_id: filterInfo.city_id === null ? undefined : filterInfo.city_id,
        visa_country_id:
          filterInfo.visa_country_id === null
            ? undefined
            : filterInfo.visa_country_id,
        show_visa_type_id:
          filterInfo.show_visa_type_id === null
            ? undefined
            : filterInfo.show_visa_type_id,
        is_have_date: filterInfo.is_have_date ? 1 : 0,
      };
      this.syncFilterParams();
      this.appointmentList = [];
      this.loadingInfo.list = true;
      window.scrollTo(0, 0);
      const task = this.requests.run('list', () =>
        Http.newPost('h5/visa_appointment_query', params),
      );
      try {
        this.appointmentList = await task;
      } catch (error) {
        // HTTP 统一反馈失败，旧任务不会覆盖列表和加载状态。
      } finally {
        if (this.requests.isLatest('list', task)) this.loadingInfo.list = false;
      }
    },
    // 页面持有条件，筛选组件只回传新对象。
    onFilterChange({ filterInfo }) {
      if (this.loadingInfo.config) return;
      this.filterInfo = filterInfo;
      return this.getData();
    },
    // 三种环境只在存在首个真实类型 ID 时携带参数，不传 0 或空值。
    gotoCountry(appointment) {
      const visa_country_id = appointment.visa_country_id;
      const visaTypeId = Number(appointment.visa_type_set?.[0]?.visa_type_id);
      // 原生协议和 iframe 消息均省略不存在的类型字段。
      const visaTypeParams = visaTypeId ? { visa_type_id: visaTypeId } : {};
      if (this.pageInfo.is_in_app) {
        const args = { visa_country_id, ...visaTypeParams };
        window.location.href = `pandavisa://jump_page?page_id=14&args=${Base64.encode(
          JSON.stringify(args),
        )}`;
      } else if (this.pageInfo.is_in_m) {
        window.parent.postMessage(
          {
            type: 'jump_page',
            data: {
              name: 'ProductList',
              params: { countryId: visa_country_id },
              query: { ...visaTypeParams, from_earliest_date: 1 },
            },
          },
          '*',
        );
      } else {
        window.location.href = `https://${env}m.11visa.com/#/product-list/${visa_country_id}?from_earliest_date=1${
          visaTypeId ? `&visa_type_id=${visaTypeId}` : ''
        }`;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.earliest-date-box {
  padding-bottom: calc(0.4rem + env(safe-area-inset-bottom));
  min-height: 100%;
  background: #fff;
  box-sizing: border-box;

  .header-box {
    position: relative;
    height: 1.3rem;
    background: linear-gradient(180deg, #9dbffc 0%, #4083ff 100%);
    &::after {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: url('https://pbs-public.oss-cn-shenzhen.aliyuncs.com/static-visa/pdvwxa/earliest-date/header-bg.png')
        no-repeat center / 100% 100%;
      content: '';
    }
    .filter {
      position: relative;
      z-index: 30;
      display: block;
      width: 100%;
    }
  }

  .content-box {
    position: relative;
    margin-top: -0.13rem;
    background: #fff;
    border-radius: 0.16rem 0.16rem 0 0;
  }
  .country-column,
  .city-column,
  .date-column,
  .action-column {
    box-sizing: border-box;
    flex-shrink: 0;
    min-width: 0;
  }
  .country-column {
    width: 30%;
    padding: 0 0.04rem 0 0.12rem;
  }
  .city-column {
    width: 14%;
    padding: 0 0.04rem;
    overflow: hidden;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .date-column {
    width: 36%;
    padding-right: 0.12rem;
    text-align: right;
  }
  .action-column {
    display: flex;
    justify-content: center;
    width: 20%;
    padding: 0 0.04rem;
  }

  .title-wrap {
    position: sticky;
    top: 0;
    z-index: 10;
    .title-box {
      display: flex;
      align-items: center;
      height: 0.46rem;
      border-radius: 0.16rem 0.16rem 0 0;
      background: #f3f6f9;
      color: #91999f;
      font-size: 0.11rem;
    }
  }

  .list-item {
    display: flex;
    align-items: center;
    min-height: 0.59rem;
    padding: 0.12rem 0;
    border-bottom: 0.005rem solid #e9eef2;
    font-size: 0.12rem;
    color: #2f333d;
    &:nth-child(even) {
      background: #f8f9fa;
    }
    .country-info {
      display: flex;
      align-items: center;
      min-height: 0.17rem;
    }
    .flag {
      flex-shrink: 0;
      width: 0.18rem;
      height: 0.14rem;
      margin-right: 0.08rem;
    }
    .country-name,
    .visa-type-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .visa-type-name {
      margin-top: 0.07rem;
      color: #91999f;
      font-size: 0.11rem;
      line-height: 0.15rem;
    }
    .appointment-date {
      display: flex;
      align-items: baseline;
      justify-content: flex-end;
      font-size: 0.14rem;
      line-height: 1.2;
      white-space: nowrap;
    }
    .week {
      margin-left: 0.06rem;
      font-size: 0.1rem;
    }
    .no-appointment {
      color: #91999f;
      font-size: 0.11rem;
    }
    .update-time {
      margin-top: 0.02rem;
      color: #91999f;
      font-size: 0.1rem;
      line-height: 1.2;
      white-space: nowrap;
    }
    .apply-link {
      display: flex;
      align-items: center;
      color: #fe595f;
      font-size: 0.11rem;
      white-space: nowrap;
    }
    .arrow-icon {
      flex-shrink: 0;
      width: 0.1rem;
      height: 0.1rem;
      margin-left: 0.01rem;
    }
  }

  .status-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 2.1rem;
    padding: 0.2rem;
    color: #91999f;
    font-size: 0.13rem;
    .empty-image {
      width: 0.99rem;
      height: 0.71rem;
      margin-bottom: 0.15rem;
      background: url('https://pbs-public.oss-cn-shenzhen.aliyuncs.com/static-visa/pdvwxa/earliest-date/nodata-img.png')
        no-repeat center / contain;
    }
  }

  .footer-box {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 20;
    box-sizing: border-box;
    background: #fff;
    padding-bottom: env(safe-area-inset-bottom);
    .tips {
      display: flex;
      align-items: flex-start;
      box-sizing: border-box;
      width: 100%;
      height: 0.4rem;
      padding: 0.05rem 0.12rem;
      background: #f8fafd;
      color: #91999f;
      font-size: 0.09rem;
      line-height: 0.14rem;
    }
    .tips-icon {
      flex-shrink: 0;
      width: 0.11rem;
      height: 0.11rem;
      margin: 0.015rem 0.04rem 0 0;
    }
  }
}

.earliest-date-box *,
.earliest-date-box *::before,
.earliest-date-box *::after {
  box-sizing: border-box;
}
.earliest-date-box img {
  object-fit: contain;
}
</style>
